import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

/**
 * Trigger detection for the `/` and `@` menus.
 *
 * Written here rather than pulled in as a dependency because what is actually needed is small
 * and the rules are opinionated: the trigger only fires at a word boundary (so a URL's `/` and
 * an email's `@` are left alone), it never fires inside code, and it releases as soon as the
 * caret leaves its own range. A general suggestion library would need all three configured
 * anyway, and this way the behaviour is visible and tested.
 */

export interface SuggestionState {
  active: boolean;
  /** Document positions of the trigger character and the caret. */
  from: number;
  to: number;
  /** What has been typed after the trigger character. */
  query: string;
  /** Viewport rect of the trigger, for positioning the menu. */
  rect: { top: number; bottom: number; left: number; right: number } | null;
}

export const EMPTY_SUGGESTION: SuggestionState = {
  active: false,
  from: 0,
  to: 0,
  query: "",
  rect: null,
};

export interface SuggestionOptions {
  /** The character that opens the menu. */
  char: string;
  /** Whether the query may contain spaces (`[` instructions do; `/` and `@` do not). */
  allowSpaces: boolean;
  /** A character that closes the menu when typed (`]` for the instruction trigger). */
  closeChar?: string;
  onChange: (state: SuggestionState) => void;
}

/**
 * The text between a trigger character and the caret, if the caret is in an open trigger.
 *
 * The trigger must sit at the start of its text block or directly after whitespace — otherwise
 * `https://x` opens a block menu and `ada@example.com` opens a mention menu, which is how a
 * suggestion feature becomes something people turn off.
 */
export function readTrigger(
  textBefore: string,
  options: { char: string; allowSpaces: boolean; closeChar?: string },
): { query: string; offset: number } | null {
  const index = textBefore.lastIndexOf(options.char);
  if (index === -1) {
    return null;
  }
  const preceding = index === 0 ? "" : textBefore.slice(index - 1, index);
  if (preceding && !/\s/.test(preceding)) {
    return null;
  }
  const query = textBefore.slice(index + options.char.length);
  if (options.closeChar && query.includes(options.closeChar)) {
    return null;
  }
  if (!options.allowSpaces && /\s/.test(query)) {
    return null;
  }
  return { query, offset: index };
}

export function createSuggestionExtension(options: SuggestionOptions) {
  const key = new PluginKey(`docit-suggestion-${options.char}`);
  return Extension.create({
    name: `docitSuggestion${options.char}`,
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key,
          view: () => ({
            update: (view: EditorView) => {
              options.onChange(readState(view, options));
            },
            destroy: () => options.onChange(EMPTY_SUGGESTION),
          }),
        }),
      ];
    },
  });
}

function readState(view: EditorView, options: SuggestionOptions): SuggestionState {
  const { selection } = view.state;
  if (!selection.empty) {
    return EMPTY_SUGGESTION;
  }
  const { $from } = selection;
  // Never inside code — a slash in a code block is a slash.
  if ($from.parent.type.spec.code) {
    return EMPTY_SUGGESTION;
  }
  const start = $from.start();
  const textBefore = $from.parent.textBetween(0, $from.parentOffset, "\n", "\n");
  const found = readTrigger(textBefore, options);
  if (!found) {
    return EMPTY_SUGGESTION;
  }
  const from = start + found.offset;
  const to = selection.from;
  let rect: SuggestionState["rect"] = null;
  try {
    const coords = view.coordsAtPos(from);
    rect = { top: coords.top, bottom: coords.bottom, left: coords.left, right: coords.right };
  } catch {
    // A position that cannot be mapped to coordinates just means no anchor; the menu centres.
  }
  return { active: true, from, to, query: found.query, rect };
}
